import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      boxShadow: "none",
    },
  },

  container: {
    margin: 0,
    containerType: "inline-size",
    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(1, 2),
    },
  },

  table: {
    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },
    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },
    "& tfoot td": {
      borderBottom: "none",
    },
  },
}));

export interface CoachLibraryTableScreenProps<T> {
  data: T[] | undefined;
  isLoading: boolean;
  mode?: "mobile" | "desktop";
  rowsPerPage: number;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  page: number;
  onPageChange: (newPage: number) => void;
  totalCount?: number;
  renderRow: (data: T, index: number) => React.ReactNode;
  renderTableHead: () => React.ReactNode;
  renderMobileCard: (data: T, index: number) => React.ReactNode;
  renderSkeletonTable?: (className?: string) => React.ReactNode;
  renderSkeletonCards?: () => React.ReactNode;
  renderMobileFilters?: () => React.ReactNode;
  emptyMessage?: string;
  className?: string;
  colCount: number;
  ariaLabel?: string;
}

export function CoachLibraryTableScreen<T>({
  data,
  isLoading,
  mode = "desktop",
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalCount = 0,
  renderRow,
  renderTableHead,
  renderMobileCard,
  renderSkeletonTable,
  renderSkeletonCards,
  renderMobileFilters,
  emptyMessage = "No records found",
  colCount,
  className,
  ariaLabel = "library table",
}: CoachLibraryTableScreenProps<T>) {
  const s = useStyles();
  const isDesktop = mode === "desktop";

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const isEmpty = !isLoading && (!data || data.length === 0);

  return (
    <Paper className={clsx(s.root, className)}>
      <Box className={s.container}>
        <Table
          className={s.table}
          sx={{ width: "100%" }}
          aria-label={ariaLabel}
        >
          {!isDesktop
            ? renderMobileFilters && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  flexWrap="wrap"
                >
                  {renderMobileFilters()}
                </Box>
              )
            : renderTableHead()}
          {isLoading ? (
            <>
              {renderSkeletonTable && isDesktop
                ? renderSkeletonTable(s.table)
                : renderSkeletonCards && !isDesktop
                  ? renderSkeletonCards()
                  : null}
            </>
          ) : (
            <TableBody>
              {isEmpty ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data?.map((item, index) => (
                    <React.Fragment key={index}>
                      {isDesktop
                        ? renderRow(item, index)
                        : renderMobileCard(item, index)}
                    </React.Fragment>
                  ))}
                </>
              )}
            </TableBody>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={colCount}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                showFirstButton
                showLastButton
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </Paper>
  );
}
